import React, { useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

export default function SyringeGraphic({ 
  units = 10, 
  maxUnits = 100, 
  concentrationMgMl = 0,
  doseMcg = 0,
  doseDisplay = null,
  agentName = "Bacteriostatic Water",
  peptideName = "Peptide"
}) {
  const [orientation, setOrientation] = useState('horizontal'); // 'horizontal' | 'vertical'
  const [isHovering, setIsHovering] = useState(false);

  const numericUnits = Number(units) || 0;
  const clampedUnits = Math.min(Math.max(numericUnits, 0), maxUnits);
  const isOverflow = numericUnits > maxUnits;
  const isUnderflow = numericUnits <= 0;
  const isTooSmall = numericUnits > 0 && numericUnits < 1.0;

  // Syringe geometry
  // Horizontal Viewbox: 920 x 220
  const barrelStartX = 150;
  const barrelEndX = 750;
  const barrelWidth = barrelEndX - barrelStartX; // 600px
  const barrelTopY = 65;
  const barrelBottomY = 155;
  const barrelHeight = barrelBottomY - barrelTopY; // 90px

  // Calculate target position in px
  const fraction = maxUnits > 0 ? clampedUnits / maxUnits : 0;
  const targetX = barrelStartX + fraction * barrelWidth;

  // Generate tick marks according to maxUnits (100, 50, 30)
  const ticks = [];
  const tickStep = maxUnits <= 30 ? 1 : (maxUnits <= 50 ? 1 : 1);
  const majorStep = maxUnits <= 30 ? 5 : (maxUnits <= 50 ? 5 : 10);
  const mediumStep = maxUnits <= 30 ? null : (maxUnits <= 50 ? null : 5);

  for (let u = 0; u <= maxUnits; u += tickStep) {
    const x = barrelStartX + (u / maxUnits) * barrelWidth;
    const isMajor = u % majorStep === 0;
    const isMedium = mediumStep && u % mediumStep === 0 && !isMajor;

    let tickLength = 10;
    if (isMajor) tickLength = 26;
    else if (isMedium) tickLength = 18;

    ticks.push({
      unit: u,
      x,
      isMajor,
      isMedium,
      length: tickLength,
      label: isMajor ? u.toString() : null
    });
  }

  // Volume in mL
  const volumeMl = (numericUnits * 0.01).toFixed(3);

  return (
    <div className="bg-slate-900/90 rounded-2xl border border-slate-800 p-4 sm:p-6 shadow-xl relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-72 h-72 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header bar of syringe graphic */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-semibold text-white tracking-wide uppercase flex items-center gap-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm shadow-red-500/50 animate-pulse" />
              Syringe Draw Visualization
            </h3>
            <span className="px-2 py-0.5 rounded text-[11px] font-mono font-medium bg-slate-800 text-sky-400 border border-slate-700">
              U-100 ({maxUnits === 100 ? '1.0 mL' : maxUnits === 50 ? '0.5 mL' : '0.3 mL'})
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Draw exactly to the <span className="text-red-400 font-semibold underline decoration-red-500/60 decoration-2">red target line</span> at <strong className="text-white font-mono">{numericUnits.toFixed(1)} Units</strong> ({volumeMl} mL)
          </p>
        </div>

        {/* View toggle */}
        <div className="flex items-center space-x-2">
          <div className="inline-flex rounded-lg bg-slate-800 p-1 border border-slate-700/80 text-xs">
            <button
              type="button"
              onClick={() => setOrientation('horizontal')}
              className={`px-2.5 py-1 rounded font-medium transition ${
                orientation === 'horizontal' 
                  ? 'bg-sky-500 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Horizontal
            </button>
            <button
              type="button"
              onClick={() => setOrientation('vertical')}
              className={`px-2.5 py-1 rounded font-medium transition ${
                orientation === 'vertical' 
                  ? 'bg-sky-500 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              Vertical View
            </button>
          </div>
        </div>
      </div>

      {/* Overflow & Warning alerts */}
      {isOverflow && (
        <div className="mb-4 flex items-center space-x-2.5 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-medium">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span>
            <strong>Capacity Exceeded:</strong> Required draw ({numericUnits.toFixed(1)} units) exceeds this {maxUnits}-unit syringe. Switch to a 100-unit syringe or reduce reconstitution volume.
          </span>
        </div>
      )}

      {isTooSmall && (
        <div className="mb-4 flex items-center space-x-2.5 px-4 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-medium">
          <Info className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span>
            <strong>Small Draw Warning:</strong> Drawing {numericUnits.toFixed(2)} units is difficult to measure accurately on a U-100 syringe. Consider adding more liquid to dilute the vial.
          </span>
        </div>
      )}

      {/* SVG Container */}
      <div className={`w-full overflow-x-auto select-none transition-all duration-300 ${orientation === 'vertical' ? 'flex justify-center py-4' : 'py-2'}`}>
        <div 
          className={`transition-all duration-300 ${orientation === 'vertical' ? 'w-64 max-w-full' : 'min-w-[680px] w-full'}`}
          style={orientation === 'vertical' ? { transform: 'rotate(90deg) scale(0.85)', transformOrigin: 'center center', margin: '140px 0' } : {}}
        >
          <svg
            viewBox="0 0 940 220"
            className="w-full h-auto drop-shadow-2xl"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <defs>
              {/* Syringe glass highlight */}
              <linearGradient id="barrelGlass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
                <stop offset="25%" stopColor="#f8fafc" stopOpacity="0.08" />
                <stop offset="70%" stopColor="#38bdf8" stopOpacity="0.04" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0.18" />
              </linearGradient>

              {/* Liquid fluid gradient */}
              <linearGradient id="fluidGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.65" />
                <stop offset="35%" stopColor="#0284c7" stopOpacity="0.5" />
                <stop offset="75%" stopColor="#0369a1" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#0c4a6e" stopOpacity="0.75" />
              </linearGradient>

              {/* Rubber stopper gradient */}
              <linearGradient id="rubberStopper" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0f172a" />
                <stop offset="30%" stopColor="#334155" />
                <stop offset="60%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#020617" />
              </linearGradient>

              {/* Needle Hub gradient (standard U-100 orange hub) */}
              <linearGradient id="needleHub" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fb923c" />
                <stop offset="40%" stopColor="#f97316" />
                <stop offset="80%" stopColor="#ea580c" />
                <stop offset="100%" stopColor="#c2410c" />
              </linearGradient>

              {/* Needle Stainless Steel */}
              <linearGradient id="needleSteel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f1f5f9" />
                <stop offset="50%" stopColor="#94a3b8" />
                <stop offset="100%" stopColor="#cbd5e1" />
              </linearGradient>

              {/* Plunger shaft gradient */}
              <linearGradient id="plungerShaft" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f8fafc" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#94a3b8" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#64748b" stopOpacity="0.7" />
              </linearGradient>

              {/* Red glow filter for indicator */}
              <filter id="redGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* 1. NEEDLE (Stainless steel cannula with beveled tip) */}
            <g id="needle">
              {/* Beveled needle point */}
              <polygon points="18,110 50,108.5 50,111.5" fill="url(#needleSteel)" />
              {/* Needle tube (31 Gauge / 8mm) */}
              <rect x="50" y="108.5" width="60" height="3" fill="url(#needleSteel)" />
            </g>

            {/* 2. NEEDLE HUB (Orange U-100 standard collar) */}
            <g id="hub">
              {/* Tapered nose */}
              <polygon points="105,103 125,98 125,122 105,117" fill="url(#needleHub)" />
              {/* Hub body */}
              <rect x="125" y="94" width="22" height="32" rx="2" fill="url(#needleHub)" />
              {/* Grip ridges on hub */}
              <line x1="130" y1="94" x2="130" y2="126" stroke="#9a3412" strokeWidth="1.5" />
              <line x1="136" y1="94" x2="136" y2="126" stroke="#9a3412" strokeWidth="1.5" />
              <line x1="142" y1="94" x2="142" y2="126" stroke="#9a3412" strokeWidth="1.5" />
              {/* Small neck joining barrel */}
              <rect x="145" y="100" width="6" height="20" fill="#cbd5e1" opacity="0.6" />
            </g>

            {/* 3. SYRINGE BARREL BASE (Glass/Polypropylene cylinder) */}
            {/* Background interior */}
            <rect 
              x={barrelStartX} 
              y={barrelTopY} 
              width={barrelWidth} 
              height={barrelHeight} 
              rx="4"
              fill="#090d16" 
            />

            {/* 4. FLUID CHAMBER (Filled to targetX) */}
            {clampedUnits > 0 && (
              <g id="fluid">
                <rect
                  x={barrelStartX}
                  y={barrelTopY + 2}
                  width={Math.max(targetX - barrelStartX, 0)}
                  height={barrelHeight - 4}
                  fill="url(#fluidGradient)"
                  className="transition-all duration-300"
                />
                {/* Fluid surface shimmer highlight */}
                <line
                  x1={barrelStartX}
                  y1={barrelTopY + 5}
                  x2={targetX}
                  y2={barrelTopY + 5}
                  stroke="#7dd3fc"
                  strokeWidth="1.5"
                  strokeOpacity="0.8"
                  className="transition-all duration-300"
                />
              </g>
            )}

            {/* 5. PLUNGER STOPPER & SHAFT (Black Rubber Gasket moving with targetX) */}
            <g id="plunger" className="transition-all duration-300">
              {/* Plunger Shaft extending out the back */}
              <rect 
                x={targetX + 38} 
                y={barrelTopY + 32} 
                width={Math.max(910 - (targetX + 38), 60)} 
                height="26" 
                fill="url(#plungerShaft)" 
                rx="2"
              />
              {/* Plunger Ribs / Spine */}
              <line 
                x1={targetX + 40} 
                y1={barrelTopY + 45} 
                x2="910" 
                y2={barrelTopY + 45} 
                stroke="#475569" 
                strokeWidth="2" 
              />
              {/* Plunger Thumb Rest (Back Flange) */}
              <rect 
                x="900" 
                y={barrelTopY + 12} 
                width="14" 
                height={barrelHeight + 42} 
                rx="3" 
                fill="#334155" 
                stroke="#64748b" 
                strokeWidth="1.5" 
              />
              <line x1="907" y1={barrelTopY + 20} x2="907" y2={barrelBottomY + 46} stroke="#1e293b" strokeWidth="2" />

              {/* Rubber Stopper / Piston (Dual sealing ring) */}
              {/* Leading edge of rubber stopper sits EXACTLY at targetX */}
              <rect 
                x={targetX} 
                y={barrelTopY + 2} 
                width="16" 
                height={barrelHeight - 4} 
                fill="url(#rubberStopper)" 
                rx="1"
              />
              {/* Stopper center waist */}
              <rect 
                x={targetX + 16} 
                y={barrelTopY + 8} 
                width="8" 
                height={barrelHeight - 16} 
                fill="#0f172a" 
              />
              {/* Stopper secondary seal ring */}
              <rect 
                x={targetX + 24} 
                y={barrelTopY + 2} 
                width="14" 
                height={barrelHeight - 4} 
                fill="url(#rubberStopper)" 
                rx="1"
              />
              {/* Rubber seal highlights */}
              <line x1={targetX + 2} y1={barrelTopY + 4} x2={targetX + 2} y2={barrelBottomY - 4} stroke="#64748b" strokeWidth="1" opacity="0.7" />
              <line x1={targetX + 26} y1={barrelTopY + 4} x2={targetX + 26} y2={barrelBottomY - 4} stroke="#64748b" strokeWidth="1" opacity="0.7" />
            </g>

            {/* 6. GRADUATION TICKS & NUMBERS */}
            <g id="graduations">
              {ticks.map((tick) => {
                const isTarget = Math.abs(tick.unit - numericUnits) < 0.1;
                return (
                  <g key={`tick-${tick.unit}`}>
                    {/* Top tick line */}
                    <line
                      x1={tick.x}
                      y1={barrelTopY}
                      x2={tick.x}
                      y2={barrelTopY + tick.length}
                      stroke={isTarget ? '#ef4444' : (tick.isMajor ? '#e2e8f0' : (tick.isMedium ? '#94a3b8' : '#475569'))}
                      strokeWidth={isTarget ? 2 : (tick.isMajor ? 1.75 : 1)}
                    />

                    {/* Bottom tick line */}
                    <line
                      x1={tick.x}
                      y1={barrelBottomY}
                      x2={tick.x}
                      y2={barrelBottomY - tick.length}
                      stroke={isTarget ? '#ef4444' : (tick.isMajor ? '#e2e8f0' : (tick.isMedium ? '#94a3b8' : '#475569'))}
                      strokeWidth={isTarget ? 2 : (tick.isMajor ? 1.75 : 1)}
                    />

                    {/* Major unit numerical labels (at bottom) */}
                    {tick.label && (
                      <text
                        x={tick.x}
                        y={barrelBottomY + 24}
                        fill={isTarget ? '#f87171' : '#94a3b8'}
                        fontSize="11"
                        fontFamily="monospace"
                        fontWeight="600"
                        textAnchor="middle"
                      >
                        {tick.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>

            {/* 7. GLASS BARREL WALLS & HIGHLIGHT (Overlaid for transparent glass sheen) */}
            <rect 
              x={barrelStartX} 
              y={barrelTopY} 
              width={barrelWidth} 
              height={barrelHeight} 
              rx="4"
              fill="url(#barrelGlass)" 
              stroke="#475569" 
              strokeWidth="1.5"
            />
            {/* Top glass reflection stroke */}
            <line 
              x1={barrelStartX + 4} 
              y1={barrelTopY + 2} 
              x2={barrelEndX - 4} 
              y2={barrelTopY + 2} 
              stroke="#ffffff" 
              strokeWidth="2" 
              strokeOpacity="0.4" 
            />

            {/* Finger Grip Flange at Barrel end (x = barrelEndX) */}
            <rect 
              x={barrelEndX - 2} 
              y={barrelTopY - 22} 
              width="10" 
              height={barrelHeight + 44} 
              rx="4" 
              fill="#1e293b" 
              stroke="#475569" 
              strokeWidth="1.5" 
            />
            <rect 
              x={barrelEndX} 
              y={barrelTopY - 20} 
              width="6" 
              height={barrelHeight + 40} 
              rx="2" 
              fill="#334155" 
            />

            {/* 8. PROMINENT RED TARGET DRAW LINE & POINTERS */}
            {numericUnits > 0 && (
              <g id="targetIndicator" filter="url(#redGlow)">
                {/* Main vertical target red line across the full barrel */}
                <line
                  x1={targetX}
                  y1={barrelTopY - 14}
                  x2={targetX}
                  y2={barrelBottomY + 14}
                  stroke="#ef4444"
                  strokeWidth="3"
                  strokeLinecap="round"
                />

                {/* Top Arrow pointing down to the red line */}
                <polygon
                  points={`${targetX},${barrelTopY - 1} ${targetX - 7},${barrelTopY - 14} ${targetX + 7},${barrelTopY - 14}`}
                  fill="#ef4444"
                />

                {/* Bottom Arrow pointing up to the red line */}
                <polygon
                  points={`${targetX},${barrelBottomY + 1} ${targetX - 7},${barrelBottomY + 14} ${targetX + 7},${barrelBottomY + 14}`}
                  fill="#ef4444"
                />
              </g>
            )}

            {/* Target Line Callout Badge (Top Header) */}
            {numericUnits > 0 && (
              <g transform={`translate(${Math.min(Math.max(targetX - 75, 140), 660)}, 10)`}>
                <rect
                  x="0"
                  y="0"
                  width="150"
                  height="34"
                  rx="8"
                  fill="#dc2626"
                  stroke="#f87171"
                  strokeWidth="1.5"
                  className="filter drop-shadow-md"
                />
                <text
                  x="75"
                  y="16"
                  fill="#ffffff"
                  fontSize="10"
                  fontWeight="800"
                  textAnchor="middle"
                  fontFamily="system-ui, sans-serif"
                  letterSpacing="0.05em"
                >
                  DRAW TO RED LINE
                </text>
                <text
                  x="75"
                  y="28"
                  fill="#fee2e2"
                  fontSize="11"
                  fontWeight="700"
                  fontFamily="monospace"
                  textAnchor="middle"
                >
                  {numericUnits.toFixed(1)} UNITS ({volumeMl} mL)
                </text>
              </g>
            )}

            {/* Zero point label */}
            <text
              x={barrelStartX}
              y={barrelTopY - 8}
              fill="#64748b"
              fontSize="10"
              fontFamily="monospace"
              textAnchor="middle"
            >
              0
            </text>

            {/* Units label badge */}
            <text
              x={barrelEndX - 20}
              y={barrelTopY - 8}
              fill="#38bdf8"
              fontSize="10"
              fontWeight="bold"
              fontFamily="monospace"
              textAnchor="end"
            >
              UNITS (U-100)
            </text>
          </svg>
        </div>
      </div>

      {/* Syringe Specs Footer */}
      <div className="mt-4 pt-4 border-t border-slate-800/80 flex flex-wrap items-center justify-between text-xs text-slate-400 gap-2">
        <div className="flex items-center space-x-3 font-mono">
          <span className="flex items-center space-x-1.5">
            <span className="w-2 h-2 rounded-full bg-cyan-400 inline-block" />
            <span>Fluid: {volumeMl} mL</span>
          </span>
          <span className="text-slate-600">•</span>
          <span className="flex items-center space-x-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
            <span>Target: {numericUnits.toFixed(1)} Units</span>
          </span>
          {(doseDisplay || doseMcg > 0) && (
            <>
              <span className="text-slate-600">•</span>
              <span className="text-slate-300">
                Dose: {doseDisplay || (doseMcg >= 1000 ? `${(doseMcg / 1000).toFixed(2)} mg` : `${doseMcg.toFixed(0)} mcg`)}
              </span>
            </>
          )}
        </div>

        <div className="text-[11px] text-slate-500 italic">
          Aligned with leading edge of the black rubber stopper
        </div>
      </div>
    </div>
  );
}
